// Learning Towers — reusable cluster overlay shown over the chat room video bg.
// Kid taps a tower → onPick(tower) fires; parent decides what to do with it.
//
// Each tower SVG is a hand-drawn pastel illustration (matches the Poodle Talk
// brand). 7 subjects, arranged village-skyline style: 4 in front, 3 in back.
import { useEffect } from "react";

// ========================================================================
// SHARED HELPERS
// ========================================================================
const TowerShadow = () => (
  <ellipse cx="60" cy="200" rx="50" ry="8" fill="#000" opacity="0.18" />
);

const WindowRow = ({ y, count = 3, fill = "#FDFBF7" }) => (
  <g>
    {Array.from({ length: count }).map((_, i) => (
      <rect
        key={i}
        x={32 + i * 16}
        y={y}
        width="8"
        height="10"
        rx="1.5"
        fill={fill}
        opacity="0.9"
      />
    ))}
  </g>
);

// ========================================================================
// 7 TOWERS — each SVG is its own subject "personality"
// ========================================================================

const EnglishTower = () => (
  <svg viewBox="0 0 120 210" className="lt-tower-svg">
    <TowerShadow />
    <rect x="20" y="60" width="80" height="135" rx="6" fill="#FFE89A" />
    <rect x="20" y="60" width="14" height="135" rx="6" fill="#E8C66A" opacity="0.55" />
    <rect x="14" y="50" width="92" height="14" rx="3" fill="#D9A93D" />
    <rect x="22" y="38" width="76" height="14" rx="3" fill="#E8B85A" />
    <path d="M52 195 V165 a8 8 0 0 1 16 0 V195 Z" fill="#9C7427" />
    <circle cx="64" cy="180" r="1.2" fill="#FFF7D6" />
    <WindowRow y={80} fill="#FFF7D6" />
    <WindowRow y={108} fill="#FFF7D6" />
    <WindowRow y={136} fill="#FFF7D6" />
    <g transform="translate(60 18)">
      <rect x="-22" y="-8" width="44" height="20" rx="2" fill="#7AB099" />
      <rect x="-22" y="-8" width="44" height="3" fill="#5A8C77" />
      <line x1="0" y1="-8" x2="0" y2="12" stroke="#FDFBF7" strokeWidth="1.5" />
      <rect x="-19" y="-4" width="16" height="2" fill="#FDFBF7" opacity="0.7" />
      <rect x="3" y="-4" width="16" height="2" fill="#FDFBF7" opacity="0.7" />
    </g>
  </svg>
);

const MathsTower = () => (
  <svg viewBox="0 0 120 210" className="lt-tower-svg">
    <TowerShadow />
    <rect x="14" y="150" width="92" height="48" rx="6" fill="#C9E8D6" />
    <rect x="14" y="150" width="14" height="48" rx="6" fill="#7CB99A" opacity="0.45" />
    <rect x="22" y="105" width="76" height="48" rx="6" fill="#B8DEC6" />
    <rect x="22" y="105" width="13" height="48" rx="6" fill="#7CB99A" opacity="0.45" />
    <rect x="32" y="60" width="56" height="48" rx="6" fill="#A9D5B6" />
    <rect x="32" y="60" width="11" height="48" rx="6" fill="#6BA88A" opacity="0.5" />
    <text x="60" y="86" textAnchor="middle" fontSize="20" fontWeight="700" fill="#3F6B55">+</text>
    <text x="40" y="135" textAnchor="middle" fontSize="14" fontWeight="700" fill="#3F6B55">7</text>
    <text x="80" y="135" textAnchor="middle" fontSize="14" fontWeight="700" fill="#3F6B55">3</text>
    <text x="60" y="180" textAnchor="middle" fontSize="14" fontWeight="700" fill="#3F6B55">π</text>
    <line x1="60" y1="62" x2="60" y2="38" stroke="#7AB099" strokeWidth="2" />
    <path d="M60 38 L80 44 L60 50 Z" fill="#5A8C77" />
  </svg>
);

const ScienceTower = () => (
  <svg viewBox="0 0 120 210" className="lt-tower-svg">
    <TowerShadow />
    <ellipse cx="60" cy="160" rx="44" ry="38" fill="#C9DFEF" />
    <ellipse cx="60" cy="160" rx="14" ry="38" fill="#7AAFC9" opacity="0.35" />
    <rect x="46" y="80" width="28" height="58" fill="#C9DFEF" />
    <rect x="46" y="80" width="9" height="58" fill="#7AAFC9" opacity="0.4" />
    <rect x="40" y="74" width="40" height="10" rx="3" fill="#7AAFC9" />
    <ellipse cx="60" cy="170" rx="36" ry="22" fill="#9CCBE8" opacity="0.7" />
    <circle cx="50" cy="155" r="3" fill="#FDFBF7" opacity="0.9" />
    <circle cx="68" cy="148" r="2" fill="#FDFBF7" opacity="0.9" />
    <circle cx="74" cy="165" r="2.5" fill="#FDFBF7" opacity="0.9" />
    <circle cx="55" cy="175" r="2" fill="#FDFBF7" opacity="0.7" />
    <path d="M50 70 Q48 60 54 54 Q60 50 56 42" stroke="#C9DFEF" strokeWidth="3" fill="none" opacity="0.6" strokeLinecap="round" />
    <path d="M64 70 Q66 62 62 56 Q58 50 64 44" stroke="#C9DFEF" strokeWidth="3" fill="none" opacity="0.6" strokeLinecap="round" />
    <path d="M72 70 Q74 60 70 54" stroke="#C9DFEF" strokeWidth="2.5" fill="none" opacity="0.5" strokeLinecap="round" />
  </svg>
);

const ArtTower = () => (
  <svg viewBox="0 0 120 210" className="lt-tower-svg">
    <TowerShadow />
    <rect x="22" y="55" width="76" height="140" rx="6" fill="#FFD6C2" />
    <rect x="22" y="65" width="76" height="18" fill="#FCEAEA" />
    <rect x="22" y="100" width="76" height="18" fill="#D8E3C0" />
    <rect x="22" y="135" width="76" height="18" fill="#C9DFEF" />
    <rect x="22" y="170" width="76" height="18" fill="#D9D0EB" />
    <rect x="22" y="55" width="13" height="140" rx="6" fill="#E89070" opacity="0.35" />
    <path d="M52 195 V172 a8 8 0 0 1 16 0 V195 Z" fill="#A14B30" />
    <circle cx="64" cy="184" r="1.2" fill="#FFF7D6" />
    <g transform="translate(60 40)">
      <path d="M0 0 q-14 -2 -16 -16 q-2 -10 8 -14 q12 -4 18 6 q12 -8 20 4 q6 10 -2 16 q4 12 -10 12 q-8 6 -18 -8 Z" fill="#F09080" />
      <circle cx="-14" cy="-18" r="3" fill="#FCEAEA" />
      <circle cx="14" cy="-12" r="2.5" fill="#D9D0EB" />
      <circle cx="6" cy="-22" r="2" fill="#FFE89A" />
    </g>
  </svg>
);

const GeographyTower = () => (
  <svg viewBox="0 0 120 210" className="lt-tower-svg">
    <TowerShadow />
    <rect x="24" y="80" width="72" height="118" rx="6" fill="#D8E3C0" />
    <rect x="24" y="80" width="13" height="118" rx="6" fill="#8FA56C" opacity="0.4" />
    <rect x="20" y="74" width="80" height="10" rx="2" fill="#A9BC85" />
    <path d="M50 198 V170 a10 10 0 0 1 20 0 V198 Z" fill="#5C6B3A" />
    <circle cx="60" cy="184" r="1.5" fill="#FFE89A" />
    <circle cx="42" cy="105" r="5" fill="#FDFBF7" opacity="0.85" />
    <circle cx="78" cy="105" r="5" fill="#FDFBF7" opacity="0.85" />
    <circle cx="60" cy="135" r="6" fill="#FDFBF7" opacity="0.85" />
    <g transform="translate(60 50)">
      <circle r="20" fill="#9CCBE8" />
      <path d="M-20 0 a20 8 0 0 0 40 0 a20 8 0 0 0 -40 0 Z" fill="#7AAFC9" opacity="0.5" />
      <ellipse cx="-6" cy="-6" rx="6" ry="4" fill="#A9D5B6" />
      <ellipse cx="8" cy="2" rx="7" ry="5" fill="#A9D5B6" />
      <ellipse cx="-2" cy="10" rx="5" ry="3" fill="#A9D5B6" />
      <line x1="0" y1="-20" x2="0" y2="20" stroke="#5A8C77" strokeWidth="0.5" opacity="0.4" />
    </g>
    <line x1="60" y1="30" x2="60" y2="22" stroke="#8FA56C" strokeWidth="2" />
    <circle cx="60" cy="20" r="2.5" fill="#FFE89A" />
  </svg>
);

const ReadingTower = () => (
  <svg viewBox="0 0 120 210" className="lt-tower-svg">
    <TowerShadow />
    <g transform="translate(60 175)">
      <rect x="-44" y="-14" width="88" height="22" rx="2" fill="#D9D0EB" />
      <rect x="-44" y="-14" width="88" height="4" fill="#9484BD" />
      <line x1="-40" y1="-14" x2="-40" y2="8" stroke="#9484BD" strokeWidth="2" />
      <rect x="-30" y="-6" width="22" height="2" fill="#FDFBF7" opacity="0.7" />
      <rect x="0" y="-6" width="30" height="2" fill="#FDFBF7" opacity="0.7" />
    </g>
    <g transform="translate(58 144)">
      <rect x="-38" y="-13" width="76" height="20" rx="2" fill="#FCEAEA" />
      <rect x="-38" y="-13" width="76" height="4" fill="#E8A8A8" />
      <line x1="34" y1="-13" x2="34" y2="7" stroke="#E8A8A8" strokeWidth="2" />
      <rect x="-26" y="-5" width="22" height="2" fill="#9C5454" opacity="0.5" />
    </g>
    <g transform="translate(62 116)">
      <rect x="-32" y="-12" width="64" height="18" rx="2" fill="#C9E8D6" />
      <rect x="-32" y="-12" width="64" height="4" fill="#7AB099" />
      <line x1="-28" y1="-12" x2="-28" y2="6" stroke="#7AB099" strokeWidth="2" />
      <rect x="-20" y="-4" width="20" height="2" fill="#3F6B55" opacity="0.5" />
    </g>
    <g transform="translate(60 90) rotate(-4)">
      <rect x="-26" y="-11" width="52" height="16" rx="2" fill="#FFE89A" />
      <rect x="-26" y="-11" width="52" height="4" fill="#D9A93D" />
      <line x1="22" y1="-11" x2="22" y2="5" stroke="#D9A93D" strokeWidth="2" />
      <rect x="-18" y="-3" width="20" height="2" fill="#9C7427" opacity="0.5" />
    </g>
    <path d="M62 78 L62 56 L66 60 L70 56 L70 78 Z" fill="#E8A8A8" />
    <circle cx="62" cy="44" r="6" fill="#FFE89A" opacity="0.8" />
    <line x1="62" y1="50" x2="62" y2="58" stroke="#9C7427" strokeWidth="1.5" />
  </svg>
);

const HistoryTower = () => (
  <svg viewBox="0 0 120 210" className="lt-tower-svg">
    <TowerShadow />
    <rect x="22" y="60" width="76" height="138" rx="5" fill="#E8DBC2" />
    <rect x="22" y="60" width="13" height="138" rx="5" fill="#C9B58E" opacity="0.55" />
    <rect x="18" y="54" width="84" height="10" rx="2" fill="#C9B58E" />
    <path d="M22 54 L60 14 L98 54 Z" fill="#D89A7A" />
    <path d="M22 54 L60 14 L60 54 Z" fill="#A8745A" opacity="0.45" />
    <line x1="60" y1="14" x2="60" y2="2" stroke="#6B5840" strokeWidth="1.5" />
    <path d="M60 2 L72 6 L60 10 Z" fill="#9484BD" />
    <circle cx="60" cy="100" r="22" fill="#FDFBF7" stroke="#A8856B" strokeWidth="2.5" />
    <circle cx="60" cy="100" r="1.5" fill="#6B5840" />
    <text x="60" y="86" textAnchor="middle" fontSize="6" fontWeight="700" fill="#6B5840">XII</text>
    <text x="78" y="103" textAnchor="middle" fontSize="6" fontWeight="700" fill="#6B5840">III</text>
    <text x="60" y="120" textAnchor="middle" fontSize="6" fontWeight="700" fill="#6B5840">VI</text>
    <text x="42" y="103" textAnchor="middle" fontSize="6" fontWeight="700" fill="#6B5840">IX</text>
    <line x1="60" y1="100" x2="60" y2="86" stroke="#6B5840" strokeWidth="1.8" strokeLinecap="round" />
    <line x1="60" y1="100" x2="71" y2="100" stroke="#6B5840" strokeWidth="1.5" strokeLinecap="round" />
    <rect x="52" y="138" width="16" height="20" rx="2" fill="#FDFBF7" opacity="0.85" />
    <line x1="60" y1="138" x2="60" y2="158" stroke="#A8856B" strokeWidth="1" />
    <line x1="52" y1="148" x2="68" y2="148" stroke="#A8856B" strokeWidth="1" />
    <path d="M50 198 V172 a10 10 0 0 1 20 0 V198 Z" fill="#6B4A30" />
    <circle cx="60" cy="186" r="1.5" fill="#FFE89A" />
  </svg>
);

// ========================================================================
// TOWER REGISTRY — single source of truth for both UI and chat prompts
// ========================================================================
export const TOWERS = [
  // Back row — flanking + centre. Science gets extra lift to peek above front.
  // Spread is wider so towers don't collide with each other or with hover labels.
  { key: "english",   label: "English",   emoji: "📚", Comp: EnglishTower,   x: -340, y: 100, scale: 0.95, z: 1,
    greeting: "Welcome to the English tower! 📚 What would you like to explore — stories, poems, spelling, or writing your own piece?" },
  { key: "science",   label: "Science",   emoji: "🔬", Comp: ScienceTower,   x: 0,    y: 200, scale: 1.0,  z: 1,
    greeting: "Welcome to the Science tower! 🔬 What shall we discover today — animals, space, the human body, weather, or something else?" },
  { key: "geography", label: "Geography", emoji: "🌍", Comp: GeographyTower, x: 340,  y: 100, scale: 0.95, z: 1,
    greeting: "Welcome to the Geography tower! 🌍 Where shall we visit — countries, oceans, mountains, or a famous city?" },
  // Front row — main 4 towers, bigger, on the ground. Wider spread so each
  // has its own breathing room.
  { key: "maths",     label: "Maths",     emoji: "🔢", Comp: MathsTower,     x: -255, y: 0,   scale: 1.15, z: 2,
    greeting: "Welcome to the Maths tower! 🔢 Shall we play with numbers, shapes, or solve a tricky puzzle?" },
  { key: "history",   label: "History",   emoji: "🏛️", Comp: HistoryTower,   x: -85,  y: 0,   scale: 1.18, z: 2,
    greeting: "Welcome to the History tower! 🏛️ Which time shall we travel to — kings & queens, Romans, World Wars, or a famous person you'd like to meet?" },
  { key: "art",       label: "Art",       emoji: "🎨", Comp: ArtTower,       x: 85,   y: 0,   scale: 1.15, z: 2,
    greeting: "Welcome to the Art tower! 🎨 Shall we draw, mix colours, or talk about a famous painter?" },
  { key: "reading",   label: "Reading",   emoji: "📖", Comp: ReadingTower,   x: 255,  y: 0,   scale: 1.15, z: 2,
    greeting: "Welcome to the Reading tower! 📖 Shall we share a tiny story together, or talk about a book you're reading?" },
];

// ========================================================================
// MAIN OVERLAY
// ========================================================================
// Props:
//   onPick(tower)  — fired when a tower is tapped. Parent decides next step.
//   onClose()      — fired when the close button or backdrop is tapped.
//   ageMode        — "kids" | "teen" — used only for the prompt copy
export default function LearningTowers({ onPick, onClose, ageMode = "kids", requiresCredits = false }) {
  // Lock body scroll while overlay is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const heading = ageMode === "teen"
    ? "Pick a subject to learn"
    : "Pick a learning tower!";
  const subhead = ageMode === "teen"
    ? "Tap any tower — your poodle will become a tutor for that subject."
    : "Tap a tower and your poodle friend will help you learn about it!";

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="learning-towers-overlay"
    >
      {/* Soft scrim — keeps the video bg + poodle visible behind, just gently
          dimmed/blurred so towers pop without hiding the magic underneath. */}
      <div className="absolute inset-0 bg-black/20 backdrop-blur-[8px]" />

      <div className="relative z-10 w-full max-w-4xl">
        {/* Heading */}
        <div className="text-center mb-4 sm:mb-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-white drop-shadow-lg" data-testid="learning-towers-heading">
            🏰 {heading}
          </h2>
          <p className="text-sm sm:text-base text-white/90 mt-2 drop-shadow">{subhead}</p>
          {requiresCredits && (
            <div className="mt-3 inline-flex items-center gap-2 bg-[#FFE89A]/95 text-[#7A5A0F] text-xs sm:text-sm px-3 py-1.5 rounded-full shadow-md backdrop-blur-sm" data-testid="learning-towers-paid-banner">
              <span>🔒</span>
              <span><strong>Tutor mode</strong> needs chat credits — ask a grown-up to unlock! ✨</span>
            </div>
          )}
        </div>

        {/* Tower cluster — floats directly over the blurred chat video bg
            (no cream card). Desktop: village-skyline absolute layout.
            Mobile (< 700px): clean 4+3 grid — bigger towers, fully tappable. */}
        <div className="relative mx-auto" data-testid="learning-towers-cluster">
          {/* drifting clouds — subtle, decorative */}
          <div className="absolute -top-2 left-8 w-32 h-12 bg-white/40 rounded-full blur-2xl pointer-events-none" />
          <div className="absolute top-6 right-12 w-40 h-14 bg-white/35 rounded-full blur-2xl pointer-events-none" />
          <div className="absolute top-16 left-1/3 w-28 h-10 bg-white/30 rounded-full blur-xl pointer-events-none" />

          {/* DESKTOP / TABLET — village skyline (absolute pos) */}
          <div className="lt-village relative w-full flex items-end justify-center" style={{ height: "440px" }}>
            <div
              className="lt-cluster relative"
              style={{
                width: "920px",
                height: "400px",
                transformOrigin: "center bottom",
              }}
            >
              {/* ground island shadow */}
              <div
                className="absolute left-1/2 -translate-x-1/2 rounded-full"
                style={{
                  bottom: "-6px",
                  width: "92%",
                  height: "44px",
                  background:
                    "radial-gradient(ellipse at center, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 70%)",
                }}
              />

              {TOWERS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => onPick(t)}
                  data-testid={`learning-tower-${t.key}-desktop`}
                  className="absolute bottom-0 left-1/2 cursor-pointer outline-none focus-visible:ring-4 focus-visible:ring-amber-300/60 rounded-2xl group"
                  style={{
                    transform: `translateX(calc(-50% + ${t.x}px)) translateY(-${t.y}px)`,
                    zIndex: t.z,
                    width: `${110 * t.scale}px`,
                    height: `${210 * t.scale}px`,
                    background: "transparent",
                    border: "none",
                    padding: 0,
                  }}
                  aria-label={`${t.label} tower`}
                >
                  <div
                    className={`lt-bob-${(t.key.charCodeAt(0) % 3) + 1} transition-transform duration-300 w-full h-full group-hover:scale-110 group-focus-visible:scale-110 drop-shadow-[0_4px_8px_rgba(0,0,0,0.25)]`}
                    style={{ transformOrigin: "bottom center" }}
                  >
                    <t.Comp />
                  </div>
                  {/* Always-visible label, sits just below the tower's ground line */}
                  <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs font-semibold text-white drop-shadow whitespace-nowrap pointer-events-none">
                    {t.emoji} {t.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* MOBILE — clean 4+3 grid, big and tappable */}
          <div className="lt-grid hidden flex-col items-center gap-4 px-2" data-testid="learning-towers-grid-mobile">
            <div className="grid grid-cols-4 gap-3 w-full max-w-md">
              {[TOWERS.find((t) => t.key === "maths"), TOWERS.find((t) => t.key === "history"), TOWERS.find((t) => t.key === "art"), TOWERS.find((t) => t.key === "reading")].map((t) => (
                <MobileTowerCell key={t.key} tower={t} onPick={onPick} />
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3 w-full max-w-sm">
              {[TOWERS.find((t) => t.key === "english"), TOWERS.find((t) => t.key === "science"), TOWERS.find((t) => t.key === "geography")].map((t) => (
                <MobileTowerCell key={t.key} tower={t} onPick={onPick} />
              ))}
            </div>
          </div>
        </div>

        {/* Close button */}
        <div className="text-center mt-4 sm:mt-6">
          <button
            onClick={onClose}
            data-testid="learning-towers-close"
            className="px-5 py-2 bg-white/90 hover:bg-white text-stone-700 rounded-full text-sm font-medium shadow-lg transition-all"
          >
            ✕ Maybe later
          </button>
        </div>
      </div>

      {/* local CSS — bobbing animation + responsive layout swap */}
      <style>{`
        .lt-tower-svg {
          width: 100%;
          height: 100%;
          display: block;
          overflow: visible;
        }
        @keyframes lt-bob {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        .lt-bob-1 { animation: lt-bob 3.4s ease-in-out infinite; }
        .lt-bob-2 { animation: lt-bob 3.8s ease-in-out infinite 0.4s; }
        .lt-bob-3 { animation: lt-bob 4.1s ease-in-out infinite 0.8s; }

        /* Cluster scaling on desktop/tablet — keep village-skyline at full
           size on big screens, gently shrink on tablet so it fits without
           bumping the chrome. */
        .lt-cluster { transform: scale(1); }
        @media (max-width: 1100px) { .lt-cluster { transform: scale(0.85); } }
        @media (max-width: 900px)  { .lt-cluster { transform: scale(0.72); } }

        /* Below 700px: hide the absolute village layout, show the clean
           4+3 grid layout instead. The grid keeps towers big and tappable. */
        @media (max-width: 699px) {
          .lt-village { display: none !important; }
          .lt-grid    { display: flex !important; }
        }
      `}</style>
    </div>
  );
}

// ---------- Mobile-only tower cell ----------
// Used inside the lt-grid layout (< 700px). Each cell is a square-ish
// tappable area that fits the tower SVG and shows its label below.
// Bobs gently like the desktop village so towers feel like they're floating.
function MobileTowerCell({ tower, onPick }) {
  const animClass = `lt-bob-${(tower.key.charCodeAt(0) % 3) + 1}`;
  return (
    <button
      onClick={() => onPick(tower)}
      data-testid={`learning-tower-${tower.key}`}
      className="flex flex-col items-center gap-1 p-2 rounded-2xl active:bg-white/10 transition-colors outline-none focus-visible:ring-4 focus-visible:ring-amber-300/60"
      aria-label={`${tower.label} tower`}
      style={{ background: "transparent", border: "none" }}
    >
      <div
        className={`${animClass} w-full drop-shadow-[0_4px_8px_rgba(0,0,0,0.25)]`}
        style={{ aspectRatio: "110 / 210", maxWidth: "90px", transformOrigin: "bottom center" }}
      >
        <tower.Comp />
      </div>
      <span className="text-xs font-semibold text-white drop-shadow whitespace-nowrap">
        {tower.emoji} {tower.label}
      </span>
    </button>
  );
}
